'use client';

import type { WorkerRequest, WorkerResponse } from './interfaces';

export class ForecastWorkerClient {
    private worker: Worker | null = null;
    private pending = new Map<string, { resolve: (res: WorkerResponse) => void; reject: (err: Error) => void }>();

    /**
     * Lazily create the underlying Worker. Kept private so callers go through
     * `call()`, which re-invokes this after a faulted worker was torn down — a
     * reused singleton client can therefore transparently recover instead of
     * posting to a dead worker (which would hang forever with no reply).
     */
    private ensureWorker(): Worker {
        if (this.worker) return this.worker;

        const worker = new Worker(new URL('./historicalForecastWorker.worker.ts', import.meta.url), {
            type: 'module',
        });

        worker.addEventListener('message', (ev: MessageEvent<WorkerResponse>) => {
            const handlers = this.pending.get(ev.data.id);
            if (handlers) {
                handlers.resolve(ev.data);
                this.pending.delete(ev.data.id);
            }
        });

        worker.addEventListener('error', (err) => {
            console.error('Worker error:', err);
            const error = new Error(err.message ?? 'Worker encountered an error');
            for (const { reject } of this.pending.values()) {
                reject(error);
            }
            this.pending.clear();
            // Tear down the faulted worker and drop the reference so the next
            // call() re-creates a fresh one. Guard on identity in case a new
            // worker was already installed by the time this handler runs.
            worker.terminate();
            if (this.worker === worker) {
                this.worker = null;
            }
        });

        this.worker = worker;
        return worker;
    }

    private uid() {
        return Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    call<T extends WorkerRequest>(request: Omit<T, 'id'>): Promise<WorkerResponse> {
        const id = this.uid();
        const msg = { id, ...request } as WorkerRequest;
        const worker = this.ensureWorker();

        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            worker.postMessage(msg);
        });
    }

    terminate() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
        // Reject any still-pending calls so their awaiters don't hang after teardown.
        if (this.pending.size > 0) {
            const error = new Error('Worker terminated');
            for (const { reject } of this.pending.values()) {
                reject(error);
            }
            this.pending.clear();
        }
    }
}
